/*
 * Copyright (c) 2025 NoorWays. All rights reserved.
 * Licensed under the terms specified in the LICENSE.txt file.
 */

import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

/**
 * Authentication composable for the web client
 * This integrates with the Pinia auth store to provide authentication state
 * and is compatible with the API client configuration
 */
export function useAuthentication() {
  const authStore = useAuthStore()

  // Reactive computed properties that integrate with the auth store
  const isAuthenticated = computed(() => authStore.isAuthenticated)
  const accessToken = computed(() => authStore.accessToken)
  const userId = computed(() => authStore.user?.id || null)

  /**
   * Get user ID - requires proper authentication
   */
  async function getUserId(): Promise<string> {
    // User must be authenticated to get user ID
    if (authStore.isAuthenticated && authStore.user?.id) {
      return authStore.user.id
    }

    // No fallback - user must be properly authenticated
    throw new Error('User must be authenticated to access this feature')
  }

  /**
   * Clear authentication state
   */
  function clearAuthentication() {
    authStore.signOut()
  }

  /**
   * Initialize authentication from storage
   */
  function initializeAuthentication() {
    // Initialize the auth store
    authStore.initializeAuth()
  }

  return {
    // State
    isAuthenticated,
    accessToken,
    userId,

    // Actions
    getUserId,
    clearAuthentication,
    initializeAuthentication
  }
}
